import React, { useState, useEffect } from 'react';
import { View, Text } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import TimelineBar from './TimelineBar';
import styles from '../../styles/pages/projectstatusStyles';
import desktopStyles from '../../styles/pages/projectstatusDesktopStyles';

interface Project {
  custom_fields: {
    id: number;
    name: string;
    value: string;
  }[];
  done_ratio: number;
}

interface TimelineChartProps {
  project: Project;
}

interface Period {
  startDate: string | null;
  endDate: string | null;
}

const TimelineChart: React.FC<TimelineChartProps> = ({ project }) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const isDesktop = useMediaQuery({ minWidth: 768 });

  const getDateFromField = (fieldName: string): string | null => {
    const field = project.custom_fields.find((f) => f.name === fieldName);
    return field?.value || null;
  };

  const contractPeriod: Period = {
    startDate: getDateFromField('시작일'),
    endDate: getDateFromField('종료일'),
  };

  const executionPeriod: Period = {
    startDate: getDateFromField('착수 시작일'),
    endDate: getDateFromField('착수 종료일'),
  };

  if (
    !contractPeriod.startDate ||
    !contractPeriod.endDate ||
    !executionPeriod.startDate ||
    !executionPeriod.endDate
  ) {
    return null;
  }

  const minStartDate = new Date(
    Math.min(
      new Date(contractPeriod.startDate).getTime(),
      new Date(executionPeriod.startDate).getTime(),
    ),
  );

  const maxEndDate = new Date(
    Math.max(
      new Date(contractPeriod.endDate).getTime(),
      new Date(executionPeriod.endDate).getTime(),
    ),
  );

  const calculatePosition = (date: string | Date): number => {
    const dateTime = date instanceof Date ? date : new Date(date);
    const startTime = minStartDate.getTime();
    const totalDuration = maxEndDate.getTime() - startTime;
    const currentPosition = dateTime.getTime() - startTime;
    return (currentPosition / totalDuration) * 100;
  };

  const formatDate = (date: string | Date): string => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  // 예상 진행률 계산 함수 수정
  const calculateExpectedProgress = () => {
    const totalDays = Math.ceil(
      (maxEndDate.getTime() - minStartDate.getTime()) / (1000 * 3600 * 24),
    );
    const elapsedDays = Math.ceil(
      (currentDate.getTime() - minStartDate.getTime()) / (1000 * 3600 * 24),
    );
    const expectedProgress = (elapsedDays / totalDays) * 100;
    // 100%를 초과하면 100%로 고정
    return Math.min(expectedProgress, 100);
  };

  const calculatePeriodProgress = (
    periodStartDate: string,
    periodEndDate: string,
  ) => {
    const startDate = new Date(periodStartDate);
    const endDate = new Date(periodEndDate);
    const actualProgress = project.done_ratio;

    // 전체 기간 중 해당 기간이 차지하는 비율 계산
    const periodStart = calculatePosition(startDate);
    const periodEnd = calculatePosition(endDate);
    const periodWidth = periodEnd - periodStart;

    if (actualProgress <= periodStart) {
      // 아직 이 기간이 시작되지 않음
      return 0;
    } else if (actualProgress >= periodEnd) {
      // 이 기간이 완전히 지남
      return 100;
    } else {
      // 이 기간 진행 중
      return ((actualProgress - periodStart) / periodWidth) * 100;
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <View style={[styles.timelineContainer, isDesktop && { paddingHorizontal: 0 }]}>
      <View style={styles.timelineHeader}>
        <Text>{formatDate(minStartDate)}</Text>
        <Text>{formatDate(maxEndDate)}</Text>
      </View>
      <View style={styles.timeline}>
        <TimelineBar
          period={contractPeriod}
          label="계약기간"
          formatDate={formatDate}
          progress={calculatePeriodProgress(
            contractPeriod.startDate!,
            contractPeriod.endDate!,
          )}
          style={[
            styles.contractPeriod,
            {
              left: `${calculatePosition(contractPeriod.startDate)}%`,
              right: `${100 - calculatePosition(contractPeriod.endDate)}%`,
            },
          ]}
        />
        <TimelineBar
          period={executionPeriod}
          label="착수기간"
          formatDate={formatDate}
          progress={calculatePeriodProgress(
            executionPeriod.startDate!,
            executionPeriod.endDate!,
          )}
          style={[
            styles.executionPeriod,
            {
              left: `${calculatePosition(executionPeriod.startDate)}%`,
              right: `${100 - calculatePosition(executionPeriod.endDate)}%`,
            },
          ]}
        />
        {/* 예상 진행률을 빨간 선으로 표시 */}
        <View
          style={[
            styles.currentDate,
            {
              left: `${calculateExpectedProgress()}%`,
              backgroundColor: '#E91212FF',
              zIndex: 10, // 가장 위에 표시
            },
          ]}
        />
        <Text
          style={[
            styles.currentDateLabel,
            {
              left: `${calculatePosition(currentDate)}%`,
              marginTop: -9,
              zIndex: 11, // 빨간 선보다 위에 표시
            },
          ]}
        >
          {formatDate(currentDate)}
        </Text>
      </View>
      <View style={styles.timelineLegend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: '#0077B6' }]} />
          <Text>계약기간</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: '#38B000' }]} />
          <Text>착수기간</Text>
        </View>
        <View style={styles.legendItem}>
          <View
            style={[
              styles.legendDot,
              { backgroundColor: 'rgba(46, 125, 50, 0.3)' },
            ]}
          />
          <Text>실제 진행률</Text>
        </View>
        <View style={styles.legendItem}>
          <View
            style={[
              styles.legendDot,
              {
                backgroundColor: '#E91212FF',
                width: 2,
                height: 20,
              },
            ]}
          />
          <Text>예상 진행률</Text>
        </View>
      </View>
    </View>
  );
};

export default TimelineChart;
